import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  filter,
  map,
  Observable,
  ReplaySubject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { getBonusTimeLeft, getDaysLeft } from '@personal-area/common';
import { AccountsFacade } from '@personal-area/global/accounts/services';
import { UnsubscribeController } from '@personal-area/global/common';
import { DepositBonusFacade } from '@personal-area/global/deposit-bonus';
import { I18nService } from '@personal-area/global/i18n';
import {
  arePromosLoading,
  GetPromos,
  getPromos,
  Promo,
  PromoRoutes,
  PromoType,
} from '@personal-area/global/promo';
import { UserFacade } from '@personal-area/global/users/services';

import { REWARDS_HUB_CONFIG } from '../token/rewards-hub-config-token';

@Component({
    selector: 'global-app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class HomeComponent extends UnsubscribeController implements OnInit {
  private readonly rewardsHubConfig = inject(REWARDS_HUB_CONFIG, { optional: true});

  public bonuses$ = this._promoItems([PromoType.bonus]);
  public contests$ = this._promoItems([PromoType.contest]);
  public services$ = this._promoItems([PromoType.service]);
  public arePromosLoading$ = this._store.select(arePromosLoading);
  public selectedAccount$ = this._accountsFacade.getSelectedAccount$;
  public depositBonusAvailability$ =
    this._depositBonusFacade.depositBonusAvailability$;
  public depositBonusList$ = this._depositBonusFacade.depositBonusList$;
  public depositBonusExpirationDate$ = new ReplaySubject<string>(1);
  public bonusTimeLeft$ = this.depositBonusExpirationDate$.pipe(
    filter(Boolean),
    take(1),
    map(getBonusTimeLeft),
  );
  public daysLeft$ = this.depositBonusExpirationDate$.pipe(
    filter(Boolean),
    take(1),
    map(getDaysLeft),
  );
  public routes = PromoRoutes;
  public types = PromoType;

  constructor(
    private readonly _store: Store,
    private readonly _translateService: TranslateService,
    private readonly _depositBonusFacade: DepositBonusFacade,
    private readonly _accountsFacade: AccountsFacade,
    private readonly _i18nService: I18nService,
    private readonly _userFacade: UserFacade,
  ) {
    super();
  }

  public ngOnInit(): void {
    if (this._translateService.langs.length > 0) {
      this._store.dispatch(GetPromos());
    }

    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => this._store.dispatch(GetPromos()));

    this.selectedAccount$
      .pipe(
        filter(Boolean),
        take(1),
        tap((account) => {
          const accountId = account.id;
          const lang = this._i18nService.languageA2;

          if (!account.isDemo) {
            this._depositBonusFacade.getDepositBonusAvailability(
              accountId,
              lang,
            );
            this._depositBonusFacade.getDepositBonusList(accountId);
          }
        }),
        switchMap((account) =>
          this.depositBonusList$.pipe(
            filter((bonusList) =>
              Boolean(bonusList?.[account.id]?.expirationDateFull),
            ),
            map((bonusList) => bonusList[account.id].expirationDateFull),
          ),
        ),
        takeUntil(this._unsubscribe),
      )
      .subscribe((expirationDateFull) => {
        this.depositBonusExpirationDate$.next(expirationDateFull);
      });
  }

  private _promoItems(types: PromoType[] = []): Observable<Promo[]> {
    return this._store
      .select(getPromos)
      .pipe(
        map((promos) => promos.filter((promo) => types.includes(promo.type))),
        map((promos) => {
          const hiddenPromoByName = this.rewardsHubConfig?.hiddenPromoByName || [];

          return promos.filter((promo) => !hiddenPromoByName.includes(promo.name));
        }),
      );
  }
}
